import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  MathDiv,
  ActionButtonsRow,
  Result,
  SvgImage,
  CopyImageButton,
  CopyHTMLButton,
  ImageDiv,
  AlertDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { MdError } from 'react-icons/md'
import { FaRegCopy } from 'react-icons/fa6'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { LoadingOverlay } from '../../../components/LoadingOverlay'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'
export function ConcreteCantilever() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(20)
  const [fyk, setFyk] = useState(500)
  const [b, setB] = useState('')
  const [h, setH] = useState('')
  const [dl, setDl] = useState('')
  const [a, setA] = useState('')
  const [c, setC] = useState('')
  const [es, setEs] = useState('')
  const [pk, setPk] = useState('')
  const [hk, setHk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [selectedTieRebar, setSelectedTieRebar] = useState('Ø8,0 mm')
  const [tieRebarQuantity, setTieRebarQuantity] = useState(0)
  const [selectedHorizontalStirrup, setSelectedHorizontalStirrup] = useState('Ø5,0 mm')
  const [horizontalStirrupQuantity, setHorizontalStirrupQuantity] = useState(0)
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const rebars = useMemo(
    () => [
      { name: 'Ø5,0 mm', area: 0.2 },
      { name: 'Ø6,3 mm', area: 0.315 },
      { name: 'Ø8,0 mm', area: 0.5 },
      { name: 'Ø10,0 mm', area: 0.8 },
      { name: 'Ø12,5 mm', area: 1.25 },
      { name: 'Ø16,0 mm', area: 2 },
      { name: 'Ø20,0 mm', area: 3.15 },
      { name: 'Ø25,0 mm', area: 5 },
    ],
    []
  )

  const HISTORY_KEY = 'concrete-cantilever-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setB(entry.b)
      setH(entry.h)
      setDl(entry.dl)
      setA(entry.a)
      setC(entry.c)
      setEs(entry.es)
      setPk(entry.pk)
      setHk(entry.hk)
      setGammaf(entry.gammaf || 1.4)
      setData(entry.data)
      setSelectedTieRebar(entry.selectedTieRebar)
      setTieRebarQuantity(entry.tieRebarQuantity)
      setSelectedHorizontalStirrup(entry.selectedHorizontalStirrup)
      setHorizontalStirrupQuantity(entry.horizontalStirrupQuantity)
      setResult(entry.result)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.b = validateField('b', b)
    newErrors.h = validateField('h', h)
    newErrors.dl = validateField('dl', dl)
    newErrors.a = validateField('a', a)
    newErrors.c = validateField('c', c)
    newErrors.es = validateField('es', es)
    newErrors.pk = validateField('pk', pk)
    newErrors.hk = validateField('hk', hk)
    newErrors.gammaf = validateField('gammaf', gammaf)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fck, fyk, b, h, dl, a, c, es, pk, hk, gammaf, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        b,
        h,
        dl,
        a,
        c,
        pd: pk * gammaf,
        hd: hk * gammaf,
        es,
        fck,
        fyk,
      }

      setData(data)

      const response = await api.post('/concrete/cantilever', data)

      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        b,
        h,
        dl,
        a,
        c,
        es,
        pk,
        hk,
        gammaf,
        selectedTieRebar,
        tieRebarQuantity,
        selectedHorizontalStirrup,
        horizontalStirrupQuantity,
        data,
        result: response.data,
      })

      const tieRebarArea = rebars.find((rebar) => rebar.name === selectedTieRebar).area
      let areaByPart = response.data.as / 2
      setTieRebarQuantity(Math.ceil(areaByPart / tieRebarArea))
      const horizontalStirrupArea = rebars.find((rebar) => rebar.name === selectedHorizontalStirrup).area
      areaByPart = response.data.ash / 2
      setHorizontalStirrupQuantity(Math.ceil(areaByPart / horizontalStirrupArea))

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  const handleTieRebarChange = (event) => {
    setSelectedTieRebar(event.target.value)
    const tieRebarArea = rebars.find((rebar) => rebar.name === event.target.value).area
    const areaByPart = result.as / 2
    setTieRebarQuantity(Math.ceil(areaByPart / tieRebarArea))
  }

  const handleHorizontalStirrupChange = (event) => {
    setSelectedHorizontalStirrup(event.target.value)
    const horizontalStirrupArea = rebars.find((rebar) => rebar.name === event.target.value).area
    const areaByPart = result.ash / 2
    setHorizontalStirrupQuantity(Math.ceil(areaByPart / horizontalStirrupArea))
  }

  const svgRef = useRef(null)
  const rebarsRef = useRef(null)
  const outputRef = useRef(null)

  const handleCopySVG = async () => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 288
      canvas.height = 332
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const handleCopyRebarsSVG = async () => {
    const svgElement = rebarsRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 654
      canvas.height = 483
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const handleCopyOutputSVG = async () => {
    const svgElement = outputRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 635
      canvas.height = 557
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const InputImage = () => {
    return (
      <SvgImage ref={svgRef} viewBox="0 0 288 332" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M114.5 220H217V106H113.5V28" stroke="black" />
        <path d="M114.5 220V312.5" stroke="black" />
        <path d="M47 313V28" stroke="black" />
        <path d="M19 28H56.5C61.3333 24 71 16.3 71 17.5C71 18.7 78 32 81.5 38.5V28H123" stroke="black" />
        <path
          d="M29 312.626H66.5C71.3333 308.626 81 300.926 81 302.126C81 303.326 88 316.626 91.5 323.126V312.626H133"
          stroke="black"
        />
        <path d="M184 96L192.66 81L175.34 81L184 96ZM182.5 45L182.5 82.5L185.5 82.5L185.5 45L182.5 45Z" fill="black" />
        <path d="M235 95L220 86.3397V103.66L235 95ZM184 96.5H221.5V93.5H184V96.5Z" fill="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="193" y="59.2727">
            {pk != 0 && pk != '' ? pk : 'Pk'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="229" y="85.2727">
            {hk != 0 && hk != '' ? hk : 'Hk'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="260" y="170.273">
            {h != 0 && h != '' ? h : 'h'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="148.365" y="62.2727">
            {a != 0 && a != '' ? a : 'a'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="end"
        >
          <tspan x="152.359" y="100.273">
            {es != 0 && es != '' ? es : 'es'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="109.887" y="130.273">
            {dl !== '' && dl != 0 ? dl : "d'"}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="182.414" y="125.273">
            {c != 0 && c != '' ? c : 'c'}
          </tspan>
        </text>
        <path
          d="M253 106L250.113 111L255.887 111L253 106ZM253 219L255.887 214L250.113 214L253 219ZM252.5 110.5L252.5 214.5L253.5 214.5L253.5 110.5L252.5 110.5Z"
          fill="black"
        />
        <path
          d="M160 95L157.113 100L162.887 100L160 95ZM160 107L162.887 102L157.113 102L160 107ZM159.5 99.5L159.5 102.5L160.5 102.5L160.5 99.5L159.5 99.5Z"
          fill="black"
        />
        <path
          d="M130 106L127.113 111L132.887 111L130 106ZM130 139L132.887 134L127.113 134L130 139ZM129.5 117.5L129.5 134.5L130.5 134.5L130.5 110.5L129.5 110.5Z"
          fill="black"
        />
        <path
          d="M114 68L119 70.8868L119 65.1132L114 68ZM184 68L179 65.1133L179 70.8868L184 68ZM118.5 68.5L179.5 68.5L179.5 67.5L118.5 67.5L118.5 68.5Z"
          fill="black"
        />
        <path
          d="M167 131L172 133.887L172 128.113L167 131ZM199 131L194 128.113L194 133.887L199 131ZM171.5 131.5L194.5 131.5L194.5 130.5L171.5 130.5L171.5 131.5Z"
          fill="black"
        />
        <line x1="221" y1="105.5" x2="272" y2="105.5" stroke="black" />
        <line x1="221" y1="219.5" x2="272" y2="219.5" stroke="black" />
        <rect x="167.5" y="95.5" width="31" height="10" stroke="black" />
        <line x1="198.5" y1="110" x2="198.5" y2="136" stroke="black" />
        <line x1="167.5" y1="110" x2="167.5" y2="136" stroke="black" />
        <line x1="208" y1="140" x2="57" y2="140" stroke="black" strokeWidth="2" />
      </SvgImage>
    )
  }

  const RebarsImage = () => {
    return (
      <SvgImage
        ref={rebarsRef}
        width="654"
        height="483"
        viewBox="0 0 654 483"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="654" height="483" fill="white" />
        <path d="M68.5 65V412.5M224 412.5V333.5H414V142H224V65" stroke="black" />
        <path
          d="M52.5 64.5H137.5L160 49C162.833 59.8333 168.6 81.2 169 80C169.4 78.8 178.833 69.1667 183.5 64.5H239"
          stroke="black"
        />
        <path d="M46 412H123L145.5 387.5L162.5 433.5L170 412H233" stroke="black" />
        <path
          d="M399 154H100C100 154 93 153.5 88.5 158.5C84 163.5 84 170 84 170V311.5"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M399.5 171H109C109 171 102 170.5 97.5 175.5C93 180.5 93 187 93 187V328.5"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M399 187.014H117C117 187.014 110 186.514 105.5 191.514C101 196.514 101 203.014 101 203.014V344.514"
          stroke="black"
          strokeWidth="2"
        />
        <circle cx="398.5" cy="153.5" r="4.5" fill="black" />
        <circle cx="398.5" cy="170.5" r="4.5" fill="black" />
        <circle cx="398.5" cy="186.5" r="4.5" fill="black" />
        <circle cx="398.5" cy="234.5" r="4.5" fill="black" />
        <circle cx="76.5" cy="234.5" r="4.5" fill="black" />
        <line x1="78" y1="234" x2="399" y2="234" stroke="black" strokeWidth="2" />
        <circle cx="398.5" cy="280.5" r="4.5" fill="black" />
        <circle cx="76.5" cy="280.5" r="4.5" fill="black" />
        <line x1="78" y1="280" x2="399" y2="280" stroke="black" strokeWidth="2" />
        <line x1="288.547" y1="153.789" x2="302.547" y2="123.789" stroke="black" />
        <line x1="299.501" y1="170.968" x2="302.501" y2="123.968" stroke="black" />
        <path d="M311 187.5L302.5 124.5H354.5" stroke="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="314" y="119">
            N1
          </tspan>
        </text>
        <line x1="199.232" y1="233.325" x2="212.463" y2="265.811" stroke="black" />
        <line x1="197.659" y1="279.634" x2="212.659" y2="265.634" stroke="black" />
        <line x1="212" y1="265.5" x2="255" y2="265.5" stroke="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="221" y="261">
            N2
          </tspan>
        </text>
        <path
          d="M586 146.014H470C470 146.014 463 145.514 458.5 150.514C454 155.514 454 162.014 454 162.014V226"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M585.5 86.016L487 86.016C487 86.016 480 85.441 475.5 91.191C471 96.941 471 104.416 471 104.416V178"
          stroke="black"
          strokeWidth="2"
        />
        <path d="M585.5 146C585.5 146 607.5 142 607.5 116C607.5 90 585.5 86 585.5 86" stroke="black" strokeWidth="2" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="484" y="78">
            N1
          </tspan>
        </text>
        <path
          d="M445.5 272.5C447 269.5 448 256.5 450 256.5C451.6 256.5 548.333 256.5 596.5 256.5L574.5 333H434L460.5 248.5H473.5"
          stroke="black"
          strokeWidth="2"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="480.5" y="249">
            N2
          </tspan>
        </text>
      </SvgImage>
    )
  }

  const OutputImage = () => {
    return (
      <SvgImage
        ref={outputRef}
        width="635"
        height="557"
        viewBox="0 0 635 557"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="635" height="557" fill="white" />
        <path d="M97 137.5H485.5V486.5H97V533.5" stroke="black" strokeWidth="2" />
        <line x1="96.6576" y1="486.636" x2="245.658" y2="346.636" stroke="black" />
        <path d="M137.5 487C137.5 487 141.5 479 137.5 469C133.5 459 128 458 128 458" stroke="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="144" y="474">
            &#x3b8;
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="527.18" y="175">
            u=2d&#x2019;
          </tspan>
        </text>
        <line x1="182.385" y1="269.68" x2="246.385" y2="346.68" stroke="black" />
        <line x1="208.385" y1="247.68" x2="272.385" y2="324.68" stroke="black" />
        <line x1="264.382" y1="198.678" x2="328.382" y2="274.678" stroke="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="12"
          letterSpacing="0em"
        >
          <tspan x="263.285" y="361">
            2d
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="251" y="361">
            &#x3c3;
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="58" y="303">
            Z
          </tspan>
        </text>
        <path
          d="M209 249L197.637 251.052L205.095 259.867L209 249ZM183.646 271.763L202.775 255.577L201.484 254.05L182.354 270.237L183.646 271.763Z"
          fill="black"
        />
        <path
          d="M227 271L215.637 273.052L223.095 281.867L227 271ZM201.646 293.763L220.775 277.577L219.484 276.05L200.354 292.237L201.646 293.763Z"
          fill="black"
        />
        <path
          d="M253 301L241.637 303.052L249.095 311.867L253 301ZM227.646 323.763L246.775 307.577L245.484 306.05L226.354 322.237L227.646 323.763Z"
          fill="black"
        />
        <path
          d="M201 323L184.165 327.074L196.111 339.616L201 323ZM160.034 364.086L192.259 333.397L190.19 331.224L157.966 361.914L160.034 364.086Z"
          fill="black"
        />
        <path
          d="M271 323L259.637 325.052L267.095 333.867L271 323ZM245.646 345.763L264.775 329.577L263.484 328.05L244.354 344.237L245.646 345.763Z"
          fill="black"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="12"
          letterSpacing="0em"
        >
          <tspan x="159.078" y="346">
            c
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="149" y="346">
            F
          </tspan>
        </text>
        <text
          transform="translate(289.812 228.626) rotate(49.1727)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="12"
          letterSpacing="0em"
        >
          <tspan x="12.1484" y="22">
            2
          </tspan>
        </text>
        <text
          transform="translate(289.812 228.626) rotate(49.1727)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="0" y="22">
            c
          </tspan>
        </text>
        <path d="M41 169L56 177.66V160.34L41 169ZM363 167.5L54.5 167.5V170.5L363 170.5V167.5Z" fill="black" />
        <path d="M368 98L376.66 83L359.34 83L368 98ZM366.5 32L366.5 84.5L369.5 84.5L369.5 32L366.5 32Z" fill="black" />
        <path d="M396 106L381 97.3397V114.66L396 106ZM333 107.5H382.5V104.5H333V107.5Z" fill="black" />
        <circle cx="97" cy="417" r="6" fill="black" />
        <line x1="155.334" y1="366.372" x2="106.334" y2="410.372" stroke="black" strokeDasharray="2 2" />
        <line x1="82" y1="417.5" x2="41" y2="417.5" stroke="black" />
        <path
          d="M76 168L70.2265 178L81.7735 178L76 168ZM76 417L81.7735 407L70.2265 407L76 417ZM75 177L75 408L77 408L77 177L75 177Z"
          fill="black"
        />
        <path
          d="M518 138L512.226 148L523.774 148L518 138ZM518 198L523.774 188L512.226 188L518 198ZM517 147L517 189L519 189L519 147L517 147Z"
          fill="black"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="12"
          letterSpacing="0em"
        >
          <tspan x="53.168" y="150">
            sd
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="41" y="150">
            R
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="12"
          letterSpacing="0em"
        >
          <tspan x="350.582" y="45">
            d
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="339" y="45">
            P
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="12"
          letterSpacing="0em"
        >
          <tspan x="315.848" y="112">
            d
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="302" y="112">
            H
          </tspan>
        </text>
        <line x1="97.6667" y1="346.627" x2="182.667" y2="270.627" stroke="black" />
        <path
          d="M298 239.5C298 239.5 332.5 207.5 353.5 183.5C374.5 159.5 369 138.5 369 138.5"
          stroke="black"
          strokeDasharray="2 2"
        />
        <rect x="331.5" y="123.5" width="76" height="14" stroke="black" />
        <line x1="264.661" y1="198.633" x2="330.661" y2="137.633" stroke="black" />
        <line x1="327.658" y1="274.635" x2="407.658" y2="199.635" stroke="black" />
        <line x1="407.5" y1="200" x2="407.5" y2="138" stroke="black" />
        <line x1="265" y1="198.5" x2="408" y2="198.5" stroke="black" />
        <line x1="428" y1="197.5" x2="594" y2="197.5" stroke="black" />
        <line x1="493" y1="137.5" x2="594" y2="137.5" stroke="black" />
      </SvgImage>
    )
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      b,
      h,
      dl,
      a,
      c,
      es,
      pk,
      hk,
      gammaf,
      selectedTieRebar,
      tieRebarQuantity,
      selectedHorizontalStirrup,
      horizontalStirrupQuantity,
      data,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concreto Consolo.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setB(jsonData.b)
        setH(jsonData.h)
        setDl(jsonData.dl)
        setA(jsonData.a)
        setC(jsonData.c)
        setEs(jsonData.es)
        setPk(jsonData.pk)
        setHk(jsonData.hk)
        setGammaf(jsonData.gammaf || 1.4)
        setSelectedTieRebar(jsonData.selectedTieRebar)
        setTieRebarQuantity(jsonData.tieRebarQuantity)
        setSelectedHorizontalStirrup(jsonData.selectedHorizontalStirrup)
        setHorizontalStirrupQuantity(jsonData.horizontalStirrupQuantity)
        setData(jsonData.data)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Consolo</span>
          </div>
          <h1>🔨 Consolo de Concreto Armado</h1>
          <p className="subtitle">
            Dimensionamento e verificação de consolos curtos de concreto armado conforme modelo de bielas e tirantes
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🔗 Modelo de Bielas e Tirantes</Badge>
            <Badge>🎯 Consolo Curto</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento e verificação de consolos curtos de concreto armado utilizando o
              modelo de bielas e tirantes, conforme a NBR 6118:2014.
            </p>
            <p>
              O cálculo considera a força vertical, força horizontal de atrito, geometria do consolo e fornece as
              armaduras necessárias (tirante, costura e estribos horizontais) para garantir a segurança estrutural.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>NBR 9062:2017</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Verificação de consolo</h2>
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <ImageDiv>
            <InputImage />
            <CopyImageButton title="Clique para copiar essa imagem" onClick={handleCopySVG}>
              <FaRegCopy />
              Copiar
            </CopyImageButton>
          </ImageDiv>
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b || ''}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra tracionada à face tracionada (cm)</span>
              <ValidatedInput
                name="dl"
                type="number"
                value={dl || ''}
                onChange={(event) => setDl(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância de aplicação da carga (cm)</span>
              <ValidatedInput
                name="a"
                type="number"
                value={a || ''}
                onChange={(event) => setA(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Comprimento do aparelho de apoio (cm)</span>
              <ValidatedInput
                name="c"
                type="number"
                value={c || ''}
                onChange={(event) => setC(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Espessura do aparelho de apoio (cm)</span>
              <ValidatedInput
                name="es"
                type="number"
                value={es || ''}
                onChange={(event) => setEs(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Vertical Característica (kN)</span>
              <ValidatedInput
                name="pk"
                type="number"
                value={pk || ''}
                onChange={(event) => setPk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Horizontal Característica (kN)</span>
              <ValidatedInput
                name="hk"
                type="number"
                value={hk || ''}
                onChange={(event) => setHk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Verificação da tensão no apoio</h3>
              <MathDiv>
                <AlertDiv>
                  <BlockMath
                    math={`\\sigma_d=\\frac{P_d}{b\\cdot c}=\\frac{${data.pd}}{${data.b}\\cdot ${data.c}}=${roundToDecimals(result.sigmad, 3)}\\, \\text{kN/cm}^2`}
                  />
                  {result.sigmad > result.fcdr && <MdError />}
                </AlertDiv>
              </MathDiv>
              <h3>Verificação da tensão na biela</h3>
              <MathDiv>
                <AlertDiv>
                  <BlockMath math={`\\sigma_{2d}=${roundToDecimals(result.sigma2d, 3)} \\, \\text{kN/cm}^2`} />
                  {result.sigma2d > result.fcdr && <MdError />}
                </AlertDiv>
              </MathDiv>
              <h3>Dimensionamentos</h3>
              <MathDiv>
                <h4>Tirante</h4>
                <InputDiv>
                  <p>Bitola para o tirante</p>
                  <Select id="tieRebarSelect" value={selectedTieRebar} onChange={handleTieRebarChange}>
                    {rebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários {tieRebarQuantity} grampos de {selectedTieRebar}
                </p>
                <h4>Estribos horizontais</h4>
                <InputDiv>
                  <p>Bitola para o estribo horizontal</p>
                  <Select
                    id="horizontalStirrupSelect"
                    value={selectedHorizontalStirrup}
                    onChange={handleHorizontalStirrupChange}
                  >
                    {rebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários {horizontalStirrupQuantity} estribos de {selectedHorizontalStirrup}
                </p>
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Resistência a compressão do concreto</h3>
              <MathDiv>
                <BlockMath math={`f_{cdr}=0.6 \\cdot \\left(1 - \\dfrac{f_{ck}}{250}\\right) \\cdot f_{cd}`} />
                <BlockMath
                  math={`f_{cdr}=0.6 \\cdot \\left(1 - \\dfrac{${data.fck}}{250}\\right) \\cdot \\dfrac{${data.fck / 10}}{1.4}`}
                />
                <BlockMath math={`\\boxed{f_{cdr}=${result.fcdr}\\, \\text{kN/cm}^2}`} />
              </MathDiv>
              <h3>Verificação da tensão no apoio</h3>
              <MathDiv>
                <AlertDiv>
                  <BlockMath
                    math={`\\boxed{\\sigma_d=\\frac{P_d}{b\\cdot c}=\\frac{${data.pd}}{${data.b}\\cdot ${data.c}}=${roundToDecimals(result.sigmad, 3)}\\, \\text{kN/cm}^2}`}
                  />
                  {result.sigmad > result.fcdr && <MdError />}
                </AlertDiv>
              </MathDiv>
              <h3>Verificação da tensão na biela</h3>
              <MathDiv>
                <BlockMath math={`F_c=\\frac{1}{\\sin{\\theta}}\\cdot \\left(P_d + H_d \\cdot \\frac{e}{a} \\right)`} />
                <BlockMath
                  math={`F_c=\\frac{1}{${result.sintheta}}\\cdot \\left(${data.pd} + ${data.hd} \\cdot \\frac{${result.e}}{${data.a}} \\right)`}
                />
                <BlockMath math={`F_c=${result.fc}\\, \\text{kN}`} />
              </MathDiv>
              <ImageDiv>
                <OutputImage />
                <CopyImageButton title="Clique para copiar essa imagem" onClick={handleCopyOutputSVG}>
                  <FaRegCopy />
                  Copiar
                </CopyImageButton>
              </ImageDiv>
              <MathDiv>
                <BlockMath math={`c_2=\\left(c+ u\\cdot cotg \\theta \\right)\\cdot \\sin{\\theta}`} />
                <BlockMath
                  math={`c_2=\\left(${data.c}+ ${result.u}\\cdot ${result.cotantheta} \\right)\\cdot ${result.sintheta}`}
                />
                <BlockMath math={`c_2=${result.c2}\\, \\text{cm}`} />
                <BlockMath
                  math={`\\sigma_{2d}=\\dfrac{F_c}{b \\cdot c_2}=\\dfrac{${result.fc}}{${b} \\cdot ${result.c2}}`}
                />
                <AlertDiv>
                  <BlockMath math={`\\boxed{\\sigma_{2d}=${roundToDecimals(result.sigma2d, 3)} \\, \\text{kN/cm}^2}`} />
                  {result.sigma2d > result.fcdr && <MdError />}
                </AlertDiv>
              </MathDiv>
              <h3>Dimensionamento do tirante (N1)</h3>
              <ImageDiv>
                <RebarsImage />
                <CopyImageButton title="Clique para copiar essa imagem" onClick={handleCopyRebarsSVG}>
                  <FaRegCopy />
                  Copiar
                </CopyImageButton>
              </ImageDiv>
              <MathDiv>
                <BlockMath math={`tg \\theta=${result.tantheta}`} />
                <BlockMath math={`R_{sd}=\\frac{P_d \\cdot a }{Z} + H_d \\cdot \\left(1 + \\frac{e}{Z}\\right)`} />
                <BlockMath
                  math={`R_{sd}=\\frac{${data.pd} \\cdot ${a} }{${result.z}} + ${data.hd} \\cdot \\left(1 + \\frac{${result.e}}{${result.z}}\\right)`}
                />
                <BlockMath math={`R_{sd}= ${result.rsd} \\, \\text{kN}`} />
                <BlockMath
                  math={`\\boxed{A_s=\\frac{R_{sd}}{f_{yd}}=\\frac{${result.rsd}}{${result.fyd}}=${result.as}\\, \\text{cm}^2}`}
                />
                <InputDiv>
                  <p>Bitola para o tirante</p>
                  <Select id="tieRebarSelect" value={selectedTieRebar} onChange={handleTieRebarChange}>
                    {rebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários {tieRebarQuantity} grampos de {selectedTieRebar}
                </p>
              </MathDiv>
              <h3>Dimensionamento dos estribos horizontais (N2)</h3>
              <MathDiv>
                <BlockMath
                  math={`\\boxed{A_{sh}=\\dfrac{A_s}{2}=\\dfrac{${result.as}}{2}=${result.ash}\\, \\text{cm}^2}`}
                />
                <InputDiv>
                  <p>Bitola para o estribo horizontal</p>
                  <Select
                    id="horizontalStirrupSelect"
                    value={selectedHorizontalStirrup}
                    onChange={handleHorizontalStirrupChange}
                  >
                    {rebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários {horizontalStirrupQuantity} estribos de {selectedHorizontalStirrup}
                </p>
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
